import { Component } from '@theme/component';

/**
 * @typedef {Object} SbsProductCardRefs
 * @property {HTMLAnchorElement} cardLink
 * @property {HTMLImageElement} [productImage]
 * @property {HTMLElement} price
 * @property {HTMLElement[]} [swatches]
 * @property {HTMLElement} [addToCart]
 * @property {HTMLElement} [addToCartText]
 */

/** @extends {Component<SbsProductCardRefs>} */
class SbsProductCard extends Component {
  #productData = null;
  #activeColor = null;
  #selectedVariantId = null;
  #abortController = null;

  connectedCallback() {
    super.connectedCallback();
    this.#parseProductData();
    this.#initActiveState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  #parseProductData() {
    const el = this.querySelector('script[data-sbs-product]');
    if (!el) return;
    try {
      this.#productData = JSON.parse(el.textContent);
    } catch { /* silent */ }
  }

  #initActiveState() {
    if (!this.#productData) return;
    this.#selectedVariantId = this.#productData.selected_variant_id;

    const active = this.refs.swatches?.find((s) => s.classList.contains('is-active'));
    if (active) {
      this.#activeColor = active.dataset.colorHandle;
    }


    // Mobile and desktop labels differ ("ADD" vs "ADD TO BAG"); the JS must not
    this.querySelectorAll('[ref="addToCartText"]').forEach((el) => {
      if (!el.dataset.originalText) el.dataset.originalText = el.textContent.trim();
    });
  }

  // ── Swatch handlers ─────────────────────────────────────────

  handleSwatchClick(event) {
    event.preventDefault();
    event.stopPropagation();

    const swatch = event.target.closest('[data-color-handle]');
    if (!swatch) return;

    const handle = swatch.dataset.colorHandle;
    if (handle === this.#activeColor) return;

    this.#selectColor(swatch, handle);
  }

  handleSwatchKeydown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    event.stopPropagation();

    const swatch = event.target.closest('[data-color-handle]');
    if (!swatch) return;

    const handle = swatch.dataset.colorHandle;
    if (handle === this.#activeColor) return;

    this.#selectColor(swatch, handle);
  }

  #selectColor(swatch, handle) {
    if (!this.#productData) return;

    this.#activeColor = handle;
    this.refs.swatches?.forEach((s) => s.classList.remove('is-active'));
    swatch.classList.add('is-active');

    const variant = this.#findVariantForColor(handle);
    if (!variant) return;

    this.#selectedVariantId = variant.id;
    this.#updateCard(variant);
  }

  #findVariantForColor(handle) {
    const idx = this.#productData.color_option_index;
    if (idx == null) return undefined;

    const matches = this.#productData.variants.filter((v) => {
      const h = v.options[idx]
        ?.toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
      return h === handle;
    });

    return matches.find((v) => v.available) || matches[0];
  }

  // ── Card update ─────────────────────────────────────────────

  #updateCard(variant) {
    this.#updateImage(variant);
    this.#updatePrice(variant);
    this.#updateEntries(variant);
    this.#updateAddToCart(variant);
    this.#updateCardLink(variant);
  }

  #updateImage(variant) {
    const img = this.refs.productImage;
    if (!img || !variant.featured_image || !(img instanceof HTMLImageElement)) return;

    const base = variant.featured_image.split('?')[0];
    img.src = `${base}?width=720`;
    img.srcset = `${base}?width=360 360w, ${base}?width=720 720w`;
  }

  #updatePrice(variant) {
    const el = this.refs.price;
    if (!el) return;

    let html = '';
    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      html += `<s class="sbs-card__compare-price">${variant.compare_at_price_formatted}</s> `;
      html += `<span class="sbs-card__sale-price">${variant.price_formatted}</span>`;
    } else {
      html += variant.price_formatted;
    }
    el.innerHTML = html;
  }

  /**
   * Keeps the sweepstakes entries banner in step with the selected variant.
   * `variant.price` is already the display price in cents (theme sale applied),
   * which is the same input shared-sweeps.liquid uses, so entries = floor(price / 100 * multiplier).
   */
  #updateEntries(variant) {
    const banner = this.querySelector('.shared-sweeps-entries-banner');
    const count = banner?.querySelector('.shared-sweeps-entries-count');
    if (!banner || !count || typeof variant.price !== 'number') return;

    const multiplier = parseFloat(banner.dataset.multiplier) || 1;
    const entries = Math.floor((variant.price / 100) * multiplier);
    count.textContent = count.textContent.replace(/\d[\d,]*(?=\s*Entries)/, entries.toLocaleString('en-US'));
  }

  #updateAddToCart(variant) {
    // Update ALL add-to-cart buttons in the card (mobile + desktop) so the
    // selected variant id stays in sync regardless of which one the user clicks.
    this.querySelectorAll('[ref="addToCart"]').forEach((btn) => {
      btn.dataset.variantId = String(variant.id);
    });

    // Restore each label's original text (cached on init) instead of forcing
    // a single string — preserves "ADD" on mobile and "ADD TO BAG" on desktop.
    this.querySelectorAll('[ref="addToCartText"]').forEach((txt) => {
      const original = txt.dataset.originalText || txt.textContent;
      txt.textContent = variant.available ? original : 'SOLD OUT';
    });
  }

  #updateCardLink(variant) {
    const link = this.refs.cardLink;
    if (!link) return;
    link.href = `${this.#productData.url}?variant=${variant.id}`;
  }

  // ── Add to cart ─────────────────────────────────────────────

  async handleAddToCart(event) {
    event.preventDefault();
    event.stopPropagation();

    if (this.hasAttribute('data-quickview-mode')) {
      this.dispatchEvent(new CustomEvent('sbs:open-quickview', {
        bubbles: true,
        detail: {
          handle: this.dataset.productHandle,
          url: this.dataset.productUrl,
          variantId: this.#selectedVariantId,
        },
      }));
      return;
    }else{
      this.style.pointerEvents = 'none';
    }

    const btn = this.refs.addToCart;
    const txt = this.refs.addToCartText;
    if (!btn || btn.classList.contains('is-adding')) return;

    const variantId = btn.dataset.variantId || String(this.#selectedVariantId);
    if (!variantId) return;

    const originalText = txt?.textContent || '';
    btn.classList.add('is-adding');
    if (txt) txt.textContent = 'ADDING...';

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const addRes = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1, properties: this.#productData.properties || {} }),
        signal: this.#abortController.signal,
      });

      if (!addRes.ok) throw new Error('Cart add failed');
      await addRes.json();

      if (txt) txt.textContent = 'ADDED \u2713';

      const cartRes = await fetch('/cart.js', { signal: this.#abortController.signal });
      const cart = await cartRes.json();

      if (!this.hasAttribute('data-silent-cart') && typeof window.SLIDECART_UPDATE === 'function') {
        window.SLIDECART_UPDATE();
        if (typeof window.SLIDECART_OPEN === 'function') {
          window.SLIDECART_OPEN();
        }
      }

      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: { resource: cart, data: { itemCount: cart.item_count } },
        })
      );

      this.style.pointerEvents = 'auto';

      const cartItems = document.querySelector('.cart-drawer__items');
      if (cartItems) {
        cartItems.scrollTop = 0;
      }

      setTimeout(() => {
        btn.classList.remove('is-adding');
        if (txt) txt.textContent = originalText;
      }, 2000);
    } catch (error) {
      this.style.pointerEvents = 'auto';

      const cartItems = document.querySelector('.cart-drawer__items');
      if (cartItems) {
        cartItems.scrollTop = 0;
      }
      if (error.name === 'AbortError') return;
      btn.classList.remove('is-adding');
      if (txt) txt.textContent = originalText;
    }
  }
}

if (!customElements.get('sbs-product-card')) {
  customElements.define('sbs-product-card', SbsProductCard);
}
